import { angleDelta, bearingDeg, elevationDeg, ftToM, haversineNm } from '../lib/geo';

export interface DevicePose {
  /** Compass heading the back camera is pointing (deg, 0=N). */
  azimuth: number;
  /** Camera pitch above the horizon (deg, -90..90). */
  elevation: number;
  /** Device roll (deg) — used to tilt the horizon. */
  roll: number;
}

export interface ProjectedAircraft {
  icao: string;
  callsign: string | null;
  x: number; // 0..1 screen fraction (left..right)
  y: number; // 0..1 screen fraction (top..bottom)
  inView: boolean;
  elevationDeg: number;
  distanceNm: number;
  altFt: number | null;
}

/**
 * Project an aircraft onto the camera frame given the user's GPS/altitude, the
 * device pose, and the camera field of view. Returns null when the aircraft has
 * no usable position.
 */
export function projectAircraft(
  user: { lat: number; lon: number; altM: number },
  ac: { icao: string; callsign: string | null; lat: number | null; lon: number | null; altBaro: number | null },
  pose: DevicePose,
  fovXDeg: number,
  fovYDeg: number,
  maxRangeNm = Infinity,
): ProjectedAircraft | null {
  if (ac.lat == null || ac.lon == null) return null;
  const distanceNm = haversineNm({ lat: user.lat, lon: user.lon }, { lat: ac.lat, lon: ac.lon });
  if (distanceNm > maxRangeNm) return null;
  const acAltM = ac.altBaro != null ? ftToM(ac.altBaro) : 0;
  const brg = bearingDeg({ lat: user.lat, lon: user.lon }, { lat: ac.lat, lon: ac.lon });
  const elev = elevationDeg({ lat: user.lat, lon: user.lon }, user.altM, { lat: ac.lat, lon: ac.lon }, acAltM);
  const dAz = angleDelta(brg, pose.azimuth);
  const dEl = elev - pose.elevation;
  const inView = Math.abs(dAz) <= fovXDeg / 2 && Math.abs(dEl) <= fovYDeg / 2 && elev > -3;
  const x = 0.5 + dAz / fovXDeg;
  const y = 0.5 - dEl / fovYDeg;
  return {
    icao: ac.icao,
    callsign: ac.callsign,
    x: clamp01(x),
    y: clamp01(y),
    inView,
    elevationDeg: elev,
    distanceNm,
    altFt: ac.altBaro,
  };
}

/** Compass cardinal markers for the HUD edges given current azimuth. */
export function cardinals(azimuth: number, fovXDeg: number): { label: string; x: number }[] {
  const dirs = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
  const out: { label: string; x: number }[] = [];
  for (let i = 0; i < 8; i++) {
    const heading = i * 45;
    const d = angleDelta(heading, azimuth);
    if (Math.abs(d) <= fovXDeg / 2) {
      out.push({ label: dirs[i], x: 0.5 + d / fovXDeg });
    }
  }
  return out;
}

function clamp01(v: number): number {
  return Math.max(0, Math.min(1, v));
}

/** Planar minimap position: north up, user at origin (0,0). Returns null when out of range. */
export function minimapPosition(
  user: { lat: number; lon: number },
  ac: { lat: number | null; lon: number | null },
  rangeNm: number,
): { x: number; y: number; distanceNm: number } | null {
  if (ac.lat == null || ac.lon == null) return null;
  const distanceNm = haversineNm({ lat: user.lat, lon: user.lon }, { lat: ac.lat, lon: ac.lon });
  if (distanceNm > rangeNm) return null;
  const brg = bearingDeg({ lat: user.lat, lon: user.lon }, { lat: ac.lat, lon: ac.lon });
  const r = distanceNm / rangeNm;
  const rad = (brg * Math.PI) / 180;
  return { x: r * Math.sin(rad), y: -r * Math.cos(rad), distanceNm };
}

/** FOV wedge corners on the minimap (north up, user at origin). */
export function minimapFovWedge(
  azimuth: number,
  fovXDeg: number,
  rangeNm: number,
): { x: number; y: number }[] {
  const half = fovXDeg / 2;
  const left = polarToMinimap(azimuth - half, rangeNm, rangeNm);
  const right = polarToMinimap(azimuth + half, rangeNm, rangeNm);
  return [{ x: 0, y: 0 }, left, right];
}

function polarToMinimap(bearing: number, distNm: number, rangeNm: number): { x: number; y: number } {
  const r = distNm / rangeNm;
  const rad = (bearing * Math.PI) / 180;
  return { x: r * Math.sin(rad), y: -r * Math.cos(rad) };
}
